import {
    call, put, select, takeEvery
} from 'redux-saga/effects';
import { addLocaleData } from 'react-intl';

import {
    getDefaultLocaleCode,
    getPreferredCurrencyCode,
    getPreferredLocaleCode, isGSAEnabled
} from '../app/bootstrap';
import { removeQueryParams, updateSearch } from '../routing/routing';
import {
    getBrandCode,
    getSupportedLocales,
    getBrandElectronicCurrencyList,
    getBrandPlasticCurrencyList, getBrandSupportedCardTypes
} from '../brand/brandSelectors';
import { restrictedPagesForLocaleUpdate } from '../routing/flowConstants';

import { getCopy } from './intlGetCopy';
import {
    SET_LOCALE,
    SET_CURRENCY,
    updateIntlMessages,
    setIntlLocale,
    updateIntlCurrency,
    completeLanguageChange,
    intlCurrencyUpdated
} from './intlModule';
import {
    getIntl,
    getIntlLocale,
    formats, getIntlCurrencyCode
} from './intlSelectors';
import { loadingAction } from '../loader/loaderModule';

import { dynamicLocaleImport } from './dynamicLocaleImport';
import { getCartCurrencyCode } from '../productCartHandler/productCartSelectors';

export const getCurrencySupportedPreferredLocale = (preferredLocaleCode, supportedLocaleCodes) => {
    const preferredLocale = supportedLocaleCodes.includes(preferredLocaleCode) ? preferredLocaleCode : null;
    return preferredLocale;
};

export const getCurrencySupportedDefaultLocale = (defaultLocaleCode, supportedLocaleCodes) => {
    const isDefaultLocale = supportedLocaleCodes.includes(defaultLocaleCode) ? defaultLocaleCode : null;
    const defaultLocale = (isDefaultLocale === null) ? supportedLocaleCodes.get(0) : isDefaultLocale;
    return defaultLocale;
};

export const checkIsPageRestrictedForLocaleUpdate = () => {
    let pageName;
    if (window && window.location) {
        pageName = (window.location.pathname.split('/').pop());
        return restrictedPagesForLocaleUpdate.some(page => page.includes(pageName));
    }
    return false;
};

export const getLocale = (hasStoredIntlData, supportedLocaleCodes, intlLocale) => {
    const isPageRestrictedForLocaleUpdate = checkIsPageRestrictedForLocaleUpdate();

    const storedLocale = (hasStoredIntlData && supportedLocaleCodes.contains(intlLocale))
        ? intlLocale
        : null;

    if (storedLocale && isPageRestrictedForLocaleUpdate) {
        return storedLocale;
    }

    const preferredLocaleCode = getPreferredLocaleCode();
    const preferredLocale = preferredLocaleCode && getCurrencySupportedPreferredLocale(preferredLocaleCode, supportedLocaleCodes);

    const defaultLocaleCode = getCurrencySupportedDefaultLocale(getDefaultLocaleCode(), supportedLocaleCodes);

    return preferredLocale || defaultLocaleCode || 'en-us';
};

export const getCurrencyDefault = (
    electronicCurrencyList, preferredCurrencyCode = null, isPlastic = null, plasticCurrencyList = null
) => {
    const selectedCurrencyList = isPlastic ? plasticCurrencyList : electronicCurrencyList;
    if (preferredCurrencyCode && selectedCurrencyList.contains(preferredCurrencyCode)) {
        return preferredCurrencyCode;
    }
    return selectedCurrencyList.first();
};

export function* getCurrency(hasStoredIntlData) {
    const intlCurrencyCode = yield select(getIntlCurrencyCode);
    const cartCurrencyCode = yield select(getCartCurrencyCode);
    const electronicCurrencyList = yield select(getBrandElectronicCurrencyList);
    const plasticCurrencyList = yield select(getBrandPlasticCurrencyList);
    const supportedCardTypes = yield select(getBrandSupportedCardTypes);
    const supportsDigital = supportedCardTypes.get('supportsDigitalCards');
    const supportsPhysical = supportedCardTypes.get('supportsPlasticCards');

    
    const isPlastic = !supportsDigital && supportsPhysical;

    const preferredCurrencyCode = getPreferredCurrencyCode();
    const storedCurrencyCode = hasStoredIntlData ? intlCurrencyCode : null;

    
    if (!isGSAEnabled()) {
        
        yield call(removeQueryParams, ['currencyCode']);
    }
    
    if (!preferredCurrencyCode && storedCurrencyCode) {
        return storedCurrencyCode;
    }

    
    
    const defaultCurrency = cartCurrencyCode || (yield call(getCurrencyDefault, electronicCurrencyList, preferredCurrencyCode, isPlastic, plasticCurrencyList));
    return defaultCurrency;
}

export function* updateCurrency(currency) {
    const electronicCurrencyList = yield select(getBrandElectronicCurrencyList);
    const plasticCurrencyList = yield select(getBrandPlasticCurrencyList);
    const intlFormats = yield select(formats);

    
    if (electronicCurrencyList.includes(currency) || plasticCurrencyList.includes(currency)) {
        yield put(updateIntlCurrency(intlFormats, currency));
        
        
        yield put(intlCurrencyUpdated());
    }
}

export function* changeLanguage({ locale = 'en-us' }) {
    const brandCode = yield select(getBrandCode);
    const messages = yield call(getCopy, brandCode, locale);
    const localeData = yield call(dynamicLocaleImport, locale);
    yield call(addLocaleData, localeData);
    yield put(updateIntlMessages(messages, locale));
    yield call(updateSearch, { locale });
    document.documentElement.setAttribute('lang', locale);
    
    
    
    yield put(completeLanguageChange());
}

export function* changeCurrency({ currency = 'USD' }) {
    yield call(updateCurrency, currency);
}

export function* initializeIntl() {
    const intl = yield select(getIntl);
    const intlLocale = yield select(getIntlLocale);
    const supportedLocaleCodes = yield select(getSupportedLocales);
    
    
    
    const hasStoredIntlData = Boolean(intl && intl.formats);
    const locale = getLocale(hasStoredIntlData, supportedLocaleCodes, intlLocale);

    yield put(setIntlLocale(locale));
    
    
    const currency = yield call(getCurrency, hasStoredIntlData);
    yield call(changeLanguage, { locale });
    yield call(changeCurrency, { currency });
}

export function* intlSagasWatcher() {
    yield put(loadingAction({ isLoading: true }));
    yield takeEvery(SET_LOCALE, changeLanguage);
    yield takeEvery(SET_CURRENCY, changeCurrency);
    yield put(loadingAction({ isLoading: false }));
}
